import { AlertTriangle, ChevronDown, Layers, Loader2, Plus, RefreshCw, Sparkles, Trash2, X } from "lucide-react";
import { useState } from "react";
import type { SowiftContextItem, SowiftContextItemCreate, SowiftContextType, SowiftProjectResponse } from "@/types";
import { CONTEXT_TYPE_LABELS, CONTEXT_TYPE_OPTIONS } from "./constants";

export function ContextTab({
  project,
  showAddContext,
  setShowAddContext,
  contextForm,
  setContextForm,
  addingContext,
  handleAddContext,
  handleDeleteContext,
  handleRetrySummary,
  deletingItem,
}: Readonly<{
  project: SowiftProjectResponse;
  showAddContext: boolean;
  setShowAddContext: (v: boolean) => void;
  contextForm: SowiftContextItemCreate;
  setContextForm: (v: SowiftContextItemCreate) => void;
  addingContext: boolean;
  handleAddContext: (e: React.FormEvent) => void;
  handleDeleteContext: (uuid: string) => void;
  handleRetrySummary: (uuid: string) => Promise<void>;
  deletingItem: string | null;
}>) {
  return (
    <div className="max-w-3xl space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-content-tertiary">Context Items ({project.contextItems.length})</h3>
        <button
          onClick={() => setShowAddContext(!showAddContext)}
          className="flex items-center gap-2 px-3 py-1.5 bg-surface-secondary hover:bg-interactive-active border border-border-secondary rounded-lg text-sm text-content-primary transition-colors"
        >
          {showAddContext ? <X className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {showAddContext ? "Cancel" : "Add Context"}
        </button>
      </div>

      {/* Add Context Form */}
      {showAddContext && (
        <form
          onSubmit={handleAddContext}
          className="bg-surface-primary border border-border-secondary rounded-xl p-5 space-y-4"
        >
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <label htmlFor="context-type" className="block text-xs text-content-quaternary mb-1">
                Type
              </label>
              <select
                id="context-type"
                value={contextForm.contextType}
                onChange={(e) =>
                  setContextForm({
                    ...contextForm,
                    contextType: e.target.value as SowiftContextType,
                  })
                }
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary text-sm focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
              >
                {CONTEXT_TYPE_OPTIONS.map((t) => (
                  <option key={t} value={t}>
                    {CONTEXT_TYPE_LABELS[t]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="context-title" className="block text-xs text-content-quaternary mb-1">
                Title
              </label>
              <input
                id="context-title"
                type="text"
                value={contextForm.title}
                onChange={(e) => setContextForm({ ...contextForm, title: e.target.value })}
                placeholder="e.g. Kickoff Meeting Notes"
                className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary text-sm focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50"
                required
              />
            </div>
          </div>
          <div>
            <label htmlFor="context-content" className="block text-xs text-content-quaternary mb-1">
              Content
            </label>
            <textarea
              id="context-content"
              value={contextForm.content}
              onChange={(e) => setContextForm({ ...contextForm, content: e.target.value })}
              placeholder="Paste meeting notes, Slack messages, requirements, etc..."
              rows={6}
              className="w-full px-3 py-2 bg-surface-secondary border border-border-secondary rounded-lg text-content-primary placeholder:text-content-quaternary text-sm focus:outline-hidden focus:ring-2 focus:ring-brand-primary/50 resize-none font-mono"
              required
            />
          </div>
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={addingContext || !contextForm.title.trim() || !contextForm.content.trim()}
              className="flex items-center gap-2 px-4 py-2 bg-brand-gradient text-white rounded-lg text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {addingContext && <Loader2 className="h-4 w-4 animate-spin" />}
              Add Context
            </button>
          </div>
        </form>
      )}

      {/* Context Item List */}
      {project.contextItems.length > 0 ? (
        <div className="space-y-3">
          {project.contextItems.map((item: SowiftContextItem) => (
            <ContextItemCard
              key={item.uuid}
              item={item}
              onDelete={() => handleDeleteContext(item.uuid)}
              onRetrySummary={() => handleRetrySummary(item.uuid)}
              deleting={deletingItem === item.uuid}
            />
          ))}
        </div>
      ) : (
        !showAddContext && (
          <div className="text-center py-12">
            <Layers className="h-10 w-10 text-content-quaternary mx-auto mb-3" />
            <p className="text-content-tertiary text-sm">
              No context items yet. Add meeting notes, Slack messages, or project briefs to get started.
            </p>
          </div>
        )
      )}
    </div>
  );
}

export function ContextItemCard({
  item,
  onDelete,
  onRetrySummary,
  deleting,
}: Readonly<{
  item: SowiftContextItem;
  onDelete: () => void;
  onRetrySummary: () => Promise<void>;
  deleting: boolean;
}>) {
  const [expanded, setExpanded] = useState(false);
  const [retrying, setRetrying] = useState(false);

  // Detect stale pending: if pending for more than 2 minutes, treat as failed
  const isStalePending =
    item.summaryStatus === "pending" &&
    !item.aiSummary &&
    Date.now() - new Date(item.createdAt).getTime() > 2 * 60 * 1000;

  // Also treat "completed" with no summary as failed (data inconsistency)
  const isCompletedWithoutSummary = item.summaryStatus === "completed" && !item.aiSummary;

  const effectiveStatus = isStalePending || isCompletedWithoutSummary ? "failed" : item.summaryStatus;

  const handleRetry = async () => {
    setRetrying(true);
    try {
      await onRetrySummary();
    } catch {
      // Error handled by parent
    } finally {
      setRetrying(false);
    }
  };

  return (
    <div className="bg-surface-primary border border-border-primary rounded-xl p-4">
      <div className="flex items-start justify-between">
        <div
          className="flex-1 cursor-pointer"
          role="button" // NOSONAR — contains nested interactive elements
          tabIndex={0}
          onClick={() => setExpanded(!expanded)}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              setExpanded(!expanded);
            }
          }}
        >
          <div className="flex items-center gap-2">
            <span className="px-2 py-0.5 rounded-sm text-xs font-medium bg-surface-tertiary text-content-secondary">
              {CONTEXT_TYPE_LABELS[item.contextType]}
            </span>
            <h4 className="text-sm font-medium text-content-primary">{item.title}</h4>
          </div>
          {(() => {
            if (item.aiSummary) {
              return (
                <div className="flex items-start gap-1.5 mt-1.5">
                  <Sparkles className="h-3 w-3 text-brand-secondary mt-0.5 shrink-0" />
                  <p className="text-xs text-content-tertiary line-clamp-2">{item.aiSummary}</p>
                </div>
              );
            }
            if (effectiveStatus === "failed") {
              return (
                <div className="flex items-center gap-1.5 mt-1.5">
                  <AlertTriangle className="h-3 w-3 text-red-400 shrink-0" />
                  <p className="text-xs text-red-400">Summary generation failed</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRetry();
                    }}
                    disabled={retrying}
                    className="flex items-center gap-1 text-xs text-brand-primary hover:text-orange-300 transition-colors disabled:opacity-50"
                  >
                    <RefreshCw className={`h-3 w-3 ${retrying ? "animate-spin" : ""}`} />
                    Retry
                  </button>
                </div>
              );
            }
            return (
              <div className="flex items-center gap-1.5 mt-1.5">
                <Loader2 className="h-3 w-3 text-content-quaternary animate-spin" />
                <p className="text-xs text-content-quaternary">Generating summary...</p>
              </div>
            );
          })()}
        </div>
        <div className="flex items-center gap-1 ml-2">
          <button
            onClick={() => setExpanded(!expanded)}
            className="p-1 text-content-quaternary hover:text-content-primary rounded-sm transition-colors"
          >
            <ChevronDown className={`h-4 w-4 transition-transform ${expanded ? "rotate-180" : ""}`} />
          </button>
          <button
            onClick={onDelete}
            disabled={deleting}
            className="p-1 text-content-quaternary hover:text-red-400 rounded-sm transition-colors disabled:opacity-50"
          >
            {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
          </button>
        </div>
      </div>
      {expanded && (
        <div className="mt-3 pt-3 border-t border-border-primary">
          <pre className="text-sm text-content-secondary whitespace-pre-wrap font-mono max-h-64 overflow-auto">
            {item.content}
          </pre>
          <p className="text-xs text-content-quaternary mt-2">Added {new Date(item.createdAt).toLocaleString()}</p>
        </div>
      )}
    </div>
  );
}
